import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import { MESSAGE_TYPES } from '../constants';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import livekitService from '../services/livekitService';

// Tab types
const TAB_TYPES = {
  GENERAL: 'general',
  USER_SPEECH: 'user_speech',
  AI_SPEECH: 'ai_speech',
  AI_INTENT: 'ai_intent',
  SYSTEM: 'system',
  CALL_INTERFACE: 'call_interface'
};

// Initial state
const initialState = {
  messages: [],
  currentTab: TAB_TYPES.GENERAL,
  userSpeechBuffer: '',
  aiSpeechBuffer: '',
  isConnected: false,
  connectionStatus: 'disconnected',
  error: null,
  messageCounter: 0 // Add counter for unique message IDs
};

// Action types
const ACTIONS = {
  ADD_MESSAGE: 'ADD_MESSAGE',
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  SET_ERROR: 'SET_ERROR',
  SET_CURRENT_TAB: 'SET_CURRENT_TAB',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  UPDATE_USER_SPEECH_BUFFER: 'UPDATE_USER_SPEECH_BUFFER',
  UPDATE_AI_SPEECH_BUFFER: 'UPDATE_AI_SPEECH_BUFFER',
  FINALIZE_USER_SPEECH: 'FINALIZE_USER_SPEECH',
  FINALIZE_AI_SPEECH: 'FINALIZE_AI_SPEECH',
  LOAD_HISTORICAL_MESSAGES: 'LOAD_HISTORICAL_MESSAGES'
};

// Reducer
function chatReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
        messageCounter: state.messageCounter + 1
      };
    
    case ACTIONS.SET_CONNECTION_STATUS:
      return {
        ...state,
        isConnected: action.payload === 'connected',
        connectionStatus: action.payload
      };
    
    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };
    
    case ACTIONS.SET_CURRENT_TAB:
      return {
        ...state,
        currentTab: action.payload
      };
    
    case ACTIONS.CLEAR_MESSAGES:
      return {
        ...state,
        messages: [],
        userSpeechBuffer: '',
        aiSpeechBuffer: '',
        messageCounter: 0
      };
    
    case ACTIONS.UPDATE_USER_SPEECH_BUFFER:
      return {
        ...state,
        userSpeechBuffer: state.userSpeechBuffer + action.payload
      };
    
    case ACTIONS.UPDATE_AI_SPEECH_BUFFER:
      return {
        ...state,
        aiSpeechBuffer: state.aiSpeechBuffer + action.payload
      };
    
    case ACTIONS.FINALIZE_USER_SPEECH:
      const finalUserText = action.payload || state.userSpeechBuffer;
      if (finalUserText.trim()) {
        const userMessage = {
          id: `user_${state.messageCounter}_${Date.now()}`,
          type: 'user_speech_chunk',
          content: finalUserText,
          timestamp: new Date().toISOString(),
          tab: 'user_speech'
        };
        return { 
          ...state, 
          messages: [...state.messages, userMessage], 
          userSpeechBuffer: '',
          messageCounter: state.messageCounter + 1
        };
      }
      return { ...state, userSpeechBuffer: '' };
    
    case ACTIONS.FINALIZE_AI_SPEECH:
      if (state.aiSpeechBuffer.trim()) {
        const aiMessage = {
          id: `ai_${state.messageCounter}_${Date.now()}`,
          type: MESSAGE_TYPES.AI_SPEECH_CHUNK,
          content: state.aiSpeechBuffer,
          timestamp: new Date().toISOString(),
          tab: TAB_TYPES.AI_SPEECH
        };
        return {
          ...state,
          messages: [...state.messages, aiMessage],
          aiSpeechBuffer: '',
          messageCounter: state.messageCounter + 1
        };
      }
      return state;

    case ACTIONS.LOAD_HISTORICAL_MESSAGES:
      // Load historical messages from backend (on resume)
      return {
        ...state,
        messages: action.payload,
        messageCounter: action.payload.length
      };

    default:
      return state;
  }
}

// Create context
const ChatContext = createContext();

// Provider component
export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const dataChannelSetupRef = useRef(false);
  const interviewEndedHandledRef = useRef(false);
  const lastAiFinalizeTimeRef = useRef(0);
  const aiSpeechBufferRef = useRef('');
  const { sessionId } = useAuth();

  const sendCurrentRuntimeData = useCallback(async () => {
    if (!sessionId) {
      console.warn('⚠️ Cannot send runtime data, no session ID available.');
      return;
    }
    try {
      console.log(`🎤 User started speaking. Sending runtime data for session: ${sessionId}`);
      await apiService.sendRuntimeData(sessionId);
      console.log('✅ Runtime data sent successfully.');
    } catch (error) {
      console.error('❌ Failed to send runtime data on speech start:', error);
    }
  }, [sessionId]);

  const handleStreamingMessage = useCallback((data) => {
    console.log('🔍 handleStreamingMessage called with type:', data.type);

    switch (data.type) {
      case MESSAGE_TYPES.USER_SPEECH_CHUNK:
        if (data.data.is_final) {
          dispatch({ type: ACTIONS.FINALIZE_USER_SPEECH, payload: data.data.text_chunk });
        } else {
          dispatch({ type: ACTIONS.UPDATE_USER_SPEECH_BUFFER, payload: data.data.text_chunk });
        }
        break;
      
      case MESSAGE_TYPES.AI_SPEECH_CHUNK:
        if (data.data.is_final) {
          // Prevent rapid duplicate finalizations (within 1 second)
          const now = Date.now();
          const timeSinceLastFinalize = now - lastAiFinalizeTimeRef.current;
          
          if (timeSinceLastFinalize < 1000) {
            console.log(`⚠️ Duplicate AI finalization detected (${timeSinceLastFinalize}ms apart), ignoring`);
            break;
          }
          
          lastAiFinalizeTimeRef.current = now;
          dispatch({ type: ACTIONS.FINALIZE_AI_SPEECH });
        } else {
          // Track buffer content
          aiSpeechBufferRef.current += data.data.text_chunk;
          dispatch({ type: ACTIONS.UPDATE_AI_SPEECH_BUFFER, payload: data.data.text_chunk });
        }
        break;
      
      case MESSAGE_TYPES.AI_INTENT:
        const intentMessage = { 
          id: `intent_${state.messageCounter}_${Date.now()}`, 
          type: MESSAGE_TYPES.AI_INTENT, 
          content: data.data, 
          timestamp: new Date().toISOString(), 
          tab: TAB_TYPES.AI_INTENT 
        };
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: intentMessage });
        break;

      case MESSAGE_TYPES.AI_INTERRUPTED:
        sendCurrentRuntimeData();
        const interruptedMessage = { 
          id: `interrupted_${state.messageCounter}_${Date.now()}`, 
          type: MESSAGE_TYPES.AI_INTERRUPTED, 
          content: data.data.message, 
          timestamp: new Date().toISOString(), 
          tab: TAB_TYPES.SYSTEM 
        };
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: interruptedMessage });
        break;
      
      case MESSAGE_TYPES.REQUEST_RUNTIME_DATA:
        sendCurrentRuntimeData();
        break;
      
      case MESSAGE_TYPES.CONNECTION:
        dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: data.status });
        break;
      
      case MESSAGE_TYPES.KEEPALIVE:
        // This is just a heartbeat, no action needed.
        break;
      
      case MESSAGE_TYPES.INTERVIEWER_JOINED:
        console.log('👋 Interviewer joined the call:', data.data);
        const interviewerJoinedMessage = { 
          id: `interviewer_joined_${state.messageCounter}_${Date.now()}`, 
          type: MESSAGE_TYPES.INTERVIEWER_JOINED, 
          content: data.data.message || 'Interviewer has joined the call', 
          timestamp: new Date().toISOString(), 
          tab: TAB_TYPES.SYSTEM 
        };
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: interviewerJoinedMessage });
        break;
      
      case 'interview_ended':
        console.log('🎯 MATCHED interview_ended case!');
        console.log('🎯 Event data:', JSON.stringify(data, null, 2));

        // Prevent duplicate handling
        if (interviewEndedHandledRef.current) {
          console.log('⚠️ Interview ended event already handled, ignoring duplicate');
          break;
        }

        console.log('⏰ Interview ended - processing now:', data.data);
        interviewEndedHandledRef.current = true;

        // Show notification to user
        console.log('📢 Showing alert to user...');
        alert('Interview Completed!\n\nThank you for your time. The interview has ended.');
        console.log('✅ Alert shown');

        // Refresh the page after a short delay to allow cleanup
        console.log('⏱️ Setting timeout to refresh page in 1 second...');
        setTimeout(() => {
          console.log('🔄 NOW REFRESHING PAGE...');
          window.location.reload();
        }, 1000);
        console.log('✅ Timeout set, waiting for refresh...');
        break;
      
      default:
        console.log('📡 Unknown message type:', data.type);
        console.log('📡 Full unknown message data:', JSON.stringify(data, null, 2));

        // Check if it's interview_ended but didn't match the case
        if (data.type && data.type.includes('interview')) {
          console.warn('⚠️ Message type contains "interview" but did not match case!');
          console.warn('⚠️ Type value:', data.type);
          console.warn('⚠️ Type typeof:', typeof data.type);
        }
    }
  }, [sendCurrentRuntimeData, state.messageCounter]);

  const setupLiveKitDataChannel = useCallback((currentSessionId) => {
    if (dataChannelSetupRef.current) {
      console.log('🔌 LiveKit data channel already set up, skipping setup.');
      return;
    }
    
    try {
      console.log(`🔗 Setting up LiveKit data channel for session: ${currentSessionId}`);
      
      // Set up data received handler
      livekitService.setOnDataReceived((payload, participant) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          console.log('📨 LiveKit: Received data message:', data);
          console.log('📨 Message type:', data.type);
          console.log('📨 Full data:', JSON.stringify(data, null, 2));

          if (data.type === 'interview_ended') {
            console.log('🚨 RECEIVED interview_ended EVENT from backend!');
          }

          handleStreamingMessage(data);
        } catch (error) {
          console.error('❌ Error parsing LiveKit data message:', error);
        }
      });
      
      // Set up connection state change handler
      livekitService.setOnConnectionStateChange((state) => {
        console.log(`🔄 LiveKit: Connection state changed: ${state}`);
        if (state === 'connected') {
          dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: 'connected' });
        } else if (state === 'disconnected') {
          dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: 'disconnected' });
        } else if (state === 'reconnecting') {
          dispatch({ type: ACTIONS.SET_CONNECTION_STATUS, payload: 'reconnecting' });
        }
      });
      
      dataChannelSetupRef.current = true;
      console.log('✅ LiveKit data channel setup completed');
      
    } catch (error) {
      console.error('❌ Error setting up LiveKit data channel:', error);
    }
  }, [handleStreamingMessage]);

  const cleanupLiveKitDataChannel = () => {
    if (dataChannelSetupRef.current) {
      console.log('🔌 Cleaning up LiveKit data channel handlers.');
      // Clear the data received handler
      livekitService.setOnDataReceived(null);
      livekitService.setOnConnectionStateChange(null);
      dataChannelSetupRef.current = false;
    }
  };


  useEffect(() => {
    // This effect now correctly depends on the sessionId.
    if (sessionId) {
      // Only setup data channel when we have a valid sessionId after login.
      setupLiveKitDataChannel(sessionId);
    }

    // This cleanup function will run when the component unmounts
    // or when sessionId changes (i.e., on logout).
    return () => {
      cleanupLiveKitDataChannel();
    };
  }, [sessionId, setupLiveKitDataChannel]);

  const changeTab = (tab) => {
    // dispatch({ type: ACTIONS.SET_CURRENT_TAB, payload: tab });
  };

  const clearMessages = () => {
    dispatch({ type: ACTIONS.CLEAR_MESSAGES });
  };

  const getMessagesForCurrentTab = () => {
    if (state.currentTab === TAB_TYPES.GENERAL) {
      return state.messages;
    }
    return state.messages.filter(msg => msg.tab === state.currentTab);
  };

  const getTabCounts = () => {
    const counts = {};
    Object.values(TAB_TYPES).forEach(tab => {
      if (tab === TAB_TYPES.GENERAL) {
        counts[tab] = state.messages.length;
      } else {
        counts[tab] = state.messages.filter(msg => msg.tab === tab).length;
      }
    });
    return counts;
  };

  const loadConversationHistory = useCallback((conversationMessages) => {
    console.log('📚 Loading conversation history:', conversationMessages.length, 'messages');

    // Transform backend conversation format to chat message format
    const formattedMessages = conversationMessages.map((msg, index) => {
      const isUser = msg.role === 'user';
      const type = isUser ? 'user_speech_chunk' : 'ai_speech_chunk';
      const tab = isUser ? 'user_speech' : 'ai_speech';

      return {
        id: `history_${msg.role}_${index}_${new Date(msg.timestamp).getTime()}`,
        type: type,
        content: msg.content,
        timestamp: msg.timestamp,
        tab: tab
      };
    });

    dispatch({ type: ACTIONS.LOAD_HISTORICAL_MESSAGES, payload: formattedMessages });
    console.log('✅ Conversation history loaded into chat');
  }, []);

  const value = {
    ...state,
    changeTab,
    clearMessages,
    getMessagesForCurrentTab,
    getTabCounts,
    loadConversationHistory,
    MESSAGE_TYPES,
    TAB_TYPES
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

// Hook to use chat context
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}